/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { isCookieFetched, mercureFetchCookie, mercureSubscribe } from "@/src/services/mercure";
import { getProductData } from "@/src/services/productService";
import { productClassNames } from "@/src/types/Entities/AbstractCase";

export type AvailableAsyncFieldsMessage = {
  availableAsyncFields?: string[] | null;
};

function parseRequiredFields(msg: AvailableAsyncFieldsMessage, requiredFields: string[]): string[] {
  const raw = msg?.availableAsyncFields;
  if (!Array.isArray(raw)) {
    return [];
  }

  const allowed = new Set<string>(requiredFields as unknown as string[]);
  return raw.filter((v): v is string => allowed.has(v));
}

type UseWaitForAsyncFieldsParams<TProduct extends { id: number; name: typeof productClassNames[number] }> = {
  product: TProduct;
  apiUri?: string;
  requiredFields: string[];
  timeoutMs?: number;
  topic?: string;
};

export function useWaitForAsyncFields<TProduct extends { id: number; name: typeof productClassNames[number] }>({
  product,
  apiUri,
  requiredFields,
  timeoutMs = 60_000,
  topic,
}: UseWaitForAsyncFieldsParams<TProduct>) {
  const [cookieOk, setCookieOk] = useState<boolean>(() => isCookieFetched());
  const [availableAsyncFields, setAvailableAsyncFields] = useState<string[]>([]);
  const [shouldWatchMercure, setShouldWatchMercure] = useState(true);
  const [shouldStartPolling, setShouldStartPolling] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const resolvedApiUri = useMemo(() => {
    return apiUri ?? getProductData(product.name, "apiUri");
  }, [apiUri, product.name]);

  const mercureTopic = useMemo(() => topic ?? `/case_data/async_data_available/${product.id}`, [topic, product.id]);

  const stopWatchingMercure = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    setShouldWatchMercure(false);
    setShouldStartPolling(true);
    stopWatchingMercure();
  }, [stopWatchingMercure]);

  const { data: refreshedProduct } = useQuery({
    queryKey: queryKeys.item(resolvedApiUri, product.id),
    queryFn: () => fetchResource(resolvedApiUri, product.id) as Promise<TProduct>,
    enabled: shouldStartPolling,
    refetchInterval: (query) => {
      const data = query.state.data;

      // @ts-ignore
      const stillWaiting = requiredFields.some((f: string) => !data || data[f] === null);
      return stillWaiting ? 2000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const displayProduct = (refreshedProduct ?? product) as TProduct;
  // @ts-ignore
  const isWaiting = requiredFields.some((f: string) => displayProduct[f] === null);

  useEffect(() => {
    stopWatchingMercure();
    setAvailableAsyncFields([]);
    setShouldWatchMercure(true);
    setShouldStartPolling(false);
    setCookieOk(isCookieFetched());
  }, [product.id, stopWatchingMercure]);

  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    if (!shouldWatchMercure) {
      return;
    }
    if (shouldStartPolling) {
      return;
    }
    if (cookieOk) {
      return;
    }

    mercureFetchCookie();

    const intervalId = setInterval(() => {
      if (isCookieFetched()) {
        setCookieOk(true);
        clearInterval(intervalId);
      }
    }, 250);

    return () => clearInterval(intervalId);
  }, [cookieOk, isWaiting, shouldStartPolling, shouldWatchMercure]);

  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    if (!shouldWatchMercure) {
      return;
    }
    if (shouldStartPolling) {
      return;
    }
    if (timeoutRef.current !== null) {
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      startPolling();
    }, timeoutMs);
  }, [isWaiting, shouldStartPolling, shouldWatchMercure, startPolling, timeoutMs]);

  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    if (!shouldWatchMercure) {
      return;
    }
    if (shouldStartPolling) {
      return;
    }
    if (!cookieOk) {
      return;
    }

    const eventSource = mercureSubscribe(mercureTopic, (msg: AvailableAsyncFieldsMessage) => {
      const incoming = parseRequiredFields(msg, requiredFields);
      if (incoming.length === 0) {
        return;
      }

      setAvailableAsyncFields((prev) => {
        const merged = new Set<string>(prev);
        for (const f of incoming) {
          merged.add(f);
        }
        return Array.from(merged);
      });
    });

    eventSourceRef.current = eventSource;

    return () => {
      eventSource.close();
      if (eventSourceRef.current === eventSource) {
        eventSourceRef.current = null;
      }
    };
  }, [cookieOk, isWaiting, mercureTopic, requiredFields, shouldStartPolling, shouldWatchMercure]);

  useEffect(() => {
    if (!isWaiting) {
      return;
    }
    if (!shouldWatchMercure) {
      return;
    }
    if (shouldStartPolling) {
      return;
    }

    const hasAll = requiredFields.every((f:string) => availableAsyncFields.includes(f));
    if (hasAll) {
      startPolling();
    }
  }, [availableAsyncFields, isWaiting, requiredFields, shouldStartPolling, shouldWatchMercure, startPolling]);

  useEffect(() => {
    if (!isWaiting) {
      setShouldWatchMercure(false);
      stopWatchingMercure();
    }
  }, [isWaiting, stopWatchingMercure]);

  return { data: displayProduct, isWaiting };
}
